import { useMemo } from 'react'
import { useEventList } from 'src/modules/event/hooks/use-event-list'
import { useUserEventStats } from 'src/modules/event/hooks/use-user-event-stats'
import { useUserPreferences } from 'src/modules/preferences/hooks/use-user-preferences'
import { paramsWithScheduledReminders } from 'src/modules/user/constants/profile-params'
import { useListUserEvents } from 'src/modules/user-events/use-list-user-events'
import { useBookingOnboarding } from 'src/modules/user/hooks/use-booking-onboarding'
import { useProfile } from 'src/modules/user/hooks/use-profile'
import { alertCampaigns } from './alert-campaigns'
import {
  AlertCampaign,
  AlertContext,
  DEFAULT_PAUSED_REMINDERS_ALERT_STATE,
  PausedRemindersAlertState,
} from './alerts.types'
import { useG2CapterraReviewAlertContext } from './g2-capterra-review-modal/use-g2-capterra-review-alert-context'
import { isAwaitingPausedRemindersData } from './paused-reminders-modal/paused-reminders-modal.campaign'
import { isAwaitingSubscriptionEndedData } from './paused-reminders-modal/subscription-ended-modal.campaign'

export function useAlertsCoordinator(
  pausedReminders: PausedRemindersAlertState = DEFAULT_PAUSED_REMINDERS_ALERT_STATE,
) {
  const profile = useProfile(paramsWithScheduledReminders)
  const bookingOnboarding = useBookingOnboarding({
    enabled: Boolean(profile.data?.is_booking_user),
  })
  const activeEvents = useEventList(
    {
      active: true,
      limit: 10,
    },
    {
      enabled: !profile.isLoading,
    },
  )
  const userEvents = useListUserEvents(undefined, {
    enabled: !profile.isLoading,
  })
  const g2CapterraReview = useG2CapterraReviewAlertContext(profile.data ?? null)
  const userEventStats = useUserEventStats({
    enabled: !profile.isLoading,
  })
  const userPreferences = useUserPreferences({
    enabled: !profile.isLoading,
  })

  const setAndForgetPromo = useMemo(
    () => ({
      manualConsentActivationCount: userEventStats.data?.manual_standing_phone_consent_activation_count ?? 0,
      autoActivateWhenClear: userPreferences.data?.user_preferences.auto_activate_when_clear ?? false,
    }),
    [userEventStats.data, userPreferences.data],
  )

  const context = useMemo<AlertContext>(
    () => ({
      profile: profile.data ?? null,
      bookingOnboarding: bookingOnboarding.data ?? null,
      activeEventsCount: activeEvents.data?.length ?? 0,
      userEvents: new Set(userEvents.data?.map((event) => event.event) ?? []),
      g2CapterraReview: g2CapterraReview.state,
      setAndForgetPromo,
      pausedReminders,
    }),
    [
      profile.data,
      bookingOnboarding.data,
      activeEvents.data,
      userEvents.data,
      g2CapterraReview.state,
      setAndForgetPromo,
      pausedReminders,
    ],
  )

  const isLoading = useMemo(
    () =>
      profile.isLoading ||
      userEvents.isLoading ||
      activeEvents.isLoading ||
      userEventStats.isLoading ||
      userPreferences.isLoading ||
      (Boolean(context.profile?.is_booking_user) && bookingOnboarding.isLoading) ||
      isAwaitingPausedRemindersData(context) ||
      isAwaitingSubscriptionEndedData(context),
    [
      activeEvents.isLoading,
      bookingOnboarding.isLoading,
      context,
      profile.isLoading,
      userEvents.isLoading,
      userEventStats.isLoading,
      userPreferences.isLoading,
    ],
  )

  const nextCampaign = useMemo<AlertCampaign | null>(() => {
    if (isLoading) {
      return null
    }

    return alertCampaigns.find((campaign) => campaign.isEligible(context)) ?? null
  }, [context, isLoading])

  return {
    context,
    isLoading,
    nextCampaign,
  }
}
